import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { cn } from "@/lib/utils";
import { KanbanCard, KanbanItem } from "./KanbanCard";
import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface KanbanColumnProps<T extends KanbanItem> {
  id: string;
  title: string;
  items: T[];
  onItemClick: (id: string) => void;
  renderItem: (item: T) => ReactNode;
  className?: string;
  total?: number;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  badge?: ReactNode;
  isRefusingDrop?: boolean;
  settlingItemId?: string;
  settleFrom?: "left" | "right";
}

/**
 * A single column in a Kanban board that supports drag-and-drop and optional pagination.
 * @param id - The column identifier (typically a status value)
 * @param title - Display title for the column header
 * @param items - Items to render in this column
 * @param onItemClick - Callback when an item card is clicked
 * @param renderItem - Render function for item content
 * @param className - Optional additional CSS classes
 * @param total - Total number of items for this column (enables "Load More" when > items.length)
 * @param isLoadingMore - Whether a "Load More" request is in progress
 * @param onLoadMore - Callback to load the next page of items
 * @param badge - Optional content shown in the header before the count
 * @param isRefusingDrop - Outlines the column in danger after it refused a dropped card, fading out when cleared
 * @param settlingItemId - The card settling back after a refused move, if it is in this column
 * @param settleFrom - The side the settling card slides back from
 * @returns A droppable Kanban column with optional Load More button
 */
export function KanbanColumn<T extends KanbanItem>({
  id,
  title,
  items,
  onItemClick,
  renderItem,
  className,
  total,
  isLoadingMore,
  onLoadMore,
  badge,
  isRefusingDrop = false,
  settlingItemId,
  settleFrom,
}: KanbanColumnProps<T>) {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: {
      type: "column",
      status: id,
    },
  });

  const itemIds = items.map((item) => item.id);

  const remainingCount = total !== undefined ? total - items.length : 0;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex flex-col flex-1 min-w-[200px] max-w-[400px] rounded-lg p-3 h-full",
        "bg-muted/30 transition-[background-color,box-shadow] duration-200 ease-out motion-reduce:transition-none",
        isOver &&
          "bg-primary/5 shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.2)]",
        isRefusingDrop &&
          "bg-danger/5 shadow-[inset_0_0_0_1px_hsl(var(--danger)/0.7)] transition-none",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="font-medium text-xs text-muted-foreground uppercase tracking-wider">
          {title}
        </h3>
        <div className="flex shrink-0 items-center gap-2">
          {badge}
          <span className="text-xs text-muted-foreground/70 tabular-nums">
            {total !== undefined ? total : items.length}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 flex-1 overflow-y-auto scrollbar-transparent py-0.5 scroll-fade-y">
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {items.map((item) => (
            <KanbanCard
              key={item.id}
              item={item}
              onClick={() => onItemClick(item.id)}
              renderContent={renderItem}
              settleFrom={item.id === settlingItemId ? settleFrom : undefined}
            />
          ))}
        </SortableContext>

        {items.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-muted-foreground/40 text-xs">No items</p>
          </div>
        )}

        {onLoadMore && total !== undefined && items.length < total && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="w-full mt-1 shrink-0 text-muted-foreground hover:text-foreground"
          >
            {isLoadingMore ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
            ) : null}
            {isLoadingMore ? "Loading..." : `Load ${remainingCount} more`}
          </Button>
        )}
      </div>
    </div>
  );
}
